import React, { useRef, useEffect, useState } from "react";

import { StyledDataGrid } from "./index.style";
import { fetchAutomaticResponseHistory } from "./DataServices/AutomaticResponsesDataServices";
import ComposeEmail from "./ComposeEmail";
import {
  IAutomaticResponseHistory,
  MemberGuarantorInfo,
  PagedResult,
} from "./Models";
import { CloseOutlined, People } from "@mui/icons-material";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import {
  AddNote,
  GetMemberAndGuarntor,
} from "./DataServices/ProspectiveTenantDataService";
import TransferTenantToAppartment from "./TransferTenantToAppartment";
import RescheduleAppointments from "./RescheduleAppointments";
import { useSearchParams } from "react-router-dom";
import Loading from "./Loading";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import { ArrowRightOnRectangleIcon } from "@heroicons/react/24/outline";
import { PaperAirplaneIcon } from "@heroicons/react/24/outline";

import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";

import { GridToolbarContainer } from "@mui/x-data-grid";
const CustomToolbar = ({
  searchTerm,
  setSearchTerm,
  setDebouncedSearchTerm,
}: {
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  setDebouncedSearchTerm: (value: string) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce logic
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400); // 400ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, setDebouncedSearchTerm]);

  // Keep focus after each render
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <GridToolbarContainer style={{ justifyContent: "flex-end" }}>
      <input
        ref={inputRef}
        type="text"
        placeholder="Search responses..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{
          margin: "8px",
          padding: "6px 12px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          fontSize: "1rem",
        }}
      />
    </GridToolbarContainer>
  );
};

const AutomaticResponseHistory: React.FC = () => {
  const [automaticResponseHistoryList, setAutomaticResponseHistoryList] =
    useState<IAutomaticResponseHistory[]>([]);
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);
  const [rowCount, setRowCount] = useState(0);

  // Get values from the query params
  var Last24Hrs = searchParams.get("Last24Hrs") === "true"; // Convert to boolean
  var IsConfirmed = searchParams.get("IsConfirmed") === "true";
  const [selectedRows, setSelectedRows] = useState<
    Set<{ email: string; dept: string }>
  >(new Set());
  const [allSelected, setAllSelected] = useState(false);
  const [showComposeEmail, setShowComposeEmail] = useState(false);
  const [allEmails, setAllEmails] = useState<
    Set<{ email: string; dept: string }>
  >(new Set());
  const [memberGuarantorInfos, setMemberGuarantorInfos] = useState<
    MemberGuarantorInfo[]
  >([]);
  const [takeNote, setTakeNote] = useState<boolean>(false);
  const [tenantNote, setTenantNote] = useState<string | null>(null);
  const [selectedTenant] = useState<IAutomaticResponseHistory | null>(null);
  const [selectedTenants, setSelectedTenants] = useState<
    IAutomaticResponseHistory[]
  >([]);
  const [
    isTransferTenantToAppartmentOpen,
    setIsTransferTenantToAppartmentOpen,
  ] = useState(false);
  const [isRescheduleAppointmentOpen, setIsRescheduleAppointmentOpen] =
    useState(false);
  const [selectedArhId, setSelectedArhId] = useState("");
  const [popoverPosition, setPopoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const isMandatoryWorkflow = (row: IAutomaticResponseHistory) =>
    row.workflowType === 0 || row.isMandatory;
  const handleCheckboxChange = (listing: IAutomaticResponseHistory) => {
    setSelectedRows((prevSelectedRows) => {
      const newSelectedRows = new Set(prevSelectedRows);
      const newSelectedTenants: IAutomaticResponseHistory[] = selectedTenants;
      const item = { email: listing.email, dept: listing.title };

      let found = false;
      newSelectedRows.forEach((selectedItem) => {
        if (
          selectedItem.email === listing.email &&
          selectedItem.dept === listing.title
        ) {
          found = true;
          newSelectedRows.delete(selectedItem);
        }
      });

      if (!found) {
        newSelectedRows.add(item);
        newSelectedTenants.push(listing);
        setSelectedTenants(newSelectedTenants);
      } else {
        setSelectedTenants((st) =>
          st.filter(
            (t) => t.email !== listing.email && t.title !== listing.title,
          ),
        );
      }

      setAllSelected(
        newSelectedRows.size === automaticResponseHistoryList.length,
      );
      return newSelectedRows;
    });
  };

  const handleisCheckboxChecked = (listing: IAutomaticResponseHistory) => {
    let found = false;
    selectedRows.forEach((selectedItem) => {
      if (
        selectedItem.email === listing.email &&
        selectedItem.dept === listing.title
      ) {
        found = true;
      }
    });
    return found;
  };

  const handleSelectAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedRows(allEmails);
      setSelectedTenants(automaticResponseHistoryList);
    } else {
      setSelectedRows(new Set());
      setSelectedTenants([]);
    }
    setAllSelected(selectAll);
  };

  const columns = [
    // ✅ Select checkbox
    {
      field: "select",
      headerName: "",
      width: 40,
      renderHeader: () => (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
        />
      ),
      renderCell: (params: any) => (
        <input
          type="checkbox"
          checked={handleisCheckboxChecked(params.row)}
          onChange={() => handleCheckboxChange(params.row)}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500"
        />
      ),
    },

    {
      field: "title",
      headerName: "Title",
      width: 240,
      // ✅ Fix: Make valueGetter match what's displayed in renderCell
      valueGetter: (params: any) => params.row.title || "No Title",
      renderCell: (params: any) => (
        <div className="flex flex-col py-4 leading-tight">
          <span className="font-medium text-sm text-gray-900">
            {params.row.title || "No Title"}
          </span>

          {params.row.moveInDate && (
            <div className="flex items-center gap-1 mt-0.5">
              <CalendarMonthIcon
                style={{ fontSize: 14 }}
                className="text-gray-400"
              />
              <span className="text-xs text-gray-500">
                {new Date(params.row.moveInDate).toLocaleDateString("en-US", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </div>
          )}
        </div>
      ),
      filterable: true,
    },
    {
      field: "name",
      headerName: "Name",
      width: 180,
      valueGetter: (params: any) => {
        const fullName = `${params.row.firstName || ""} ${
          params.row.lastName || ""
        }`.trim();
        return fullName || "No Name";
      },
      renderCell: (params: any) => {
        const fullName = params.value;
        return (
          <div className="cursor-pointer truncate text-sm font-medium text-gray-800">
            {fullName}
          </div>
        );
      },
      filterable: true,
    },
    {
      field: "contact",
      headerName: "Contact",
      width: 220,

      valueGetter: (params: any) => {
        const email = params.row.email || "";
        const phone = params.row.phone || "";
        return `${email} ${phone}`.trim();
      },

      renderCell: (params: any) => (
        <div className="flex flex-col gap-1 leading-tight w-full min-w-0">
          <div
            className="flex items-center gap-1 min-w-0"
            title={params.row.email}
          >
            <EmailIcon
              style={{ fontSize: 14 }}
              className="text-gray-400 flex-shrink-0"
            />
            <span className="text-sm text-gray-700 truncate min-w-0">
              {params.row.email}
            </span>
          </div>

          {params.row.phone && (
            <div className="flex items-center gap-1 min-w-0">
              <PhoneIphoneIcon
                style={{ fontSize: 14 }}
                className="text-gray-400 flex-shrink-0"
              />
              <span className="text-xs text-gray-500 truncate min-w-0">
                {params.row.phone}
              </span>
            </div>
          )}
        </div>
      ),

      filterable: true,
    },
    {
      field: "memberGuarntorInfo",
      headerName: "Members",
      width: 80,
      align: "center",
      renderCell: (params: any) => (
        <span
          onClick={(e) => {
            const rect = (e.target as HTMLElement).getBoundingClientRect();
            setPopoverPosition({ x: rect.right + 10, y: rect.top });
            setSelectedArhId(params.row?.id || "");
            setLoading(true);
            GetMemberAndGuarntor(params.row.id).then((data) => {
              setMemberGuarantorInfos(data);
              setLoading(false);
            });
          }}
          className={`cursor-pointer hover:text-blue-600 ${
            params.row.id === selectedArhId && "text-blue-600"
          }`}
        >
          <People fontSize="small" />
        </span>
      ),
    },

    {
      field: "appointment",
      headerName: "Appointment",
      headerAlign: "center",
      align: "center",
      width: 230,
      renderCell: (params: any) => {
        const appointmentStatus = params.row.appointmentStatus || "";
        const showTiming =
          appointmentStatus === "Timing" ||
          (!appointmentStatus && !isMandatoryWorkflow(params.row));

        if (appointmentStatus === "MailNotSentYet") {
          return (
            <div className="flex h-full w-full flex-col items-center justify-center text-center leading-tight">
              <span className="text-sm font-medium text-red-500">
                Appointment Mail Not Sent Yet
              </span>
            </div>
          );
        }

        if (appointmentStatus === "NoSlots") {
          return (
            <div className="flex h-full w-full flex-col items-center justify-center text-center leading-tight">
              <span className="text-sm font-medium text-orange-600">
                No slot Available right now
              </span>
            </div>
          );
        }

        if (isMandatoryWorkflow(params.row) && !showTiming) {
          return (
            <div className="flex h-full w-full flex-col items-center justify-center text-center leading-tight">
              <span className="text-sm font-medium text-red-500">
                Appointment Mail Not Sent Yet
              </span>
            </div>
          );
        }

        const start = params.row.startTime
          ? new Date(params.row.startTime)
          : null;
        const end = params.row.endTime ? new Date(params.row.endTime) : null;

        const formattedDate = start
          ? start.toLocaleDateString("en-US", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "No Date";

        const startTime = start
          ? start.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            })
          : "--";

        const endTime = end
          ? end.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            })
          : "--";

        return (
          <div className="flex h-full w-full flex-col items-center justify-center text-center leading-tight">
            <span className="text-sm font-medium text-gray-900">
              {formattedDate}
            </span>

            <div className="flex items-center justify-center  bg-gray-200 text-xs mt-1">
              <span className="px-1 py-0.5 rounded  text-gray-800">
                {startTime}
              </span>
              <span className="text-gray-400">→</span>
              <span className="px-1 py-0.5 rounded  text-gray-800">
                {endTime}
              </span>
            </div>
          </div>
        );
      },
    },

    {
      field: "sentDate",
      headerName: "Sent Date",
      width: 180,
      renderCell: (params: any) => {
        const date = params.row.sentDate ? new Date(params.row.sentDate) : null;

        const formattedDate = date
          ? date.toLocaleDateString("en-US", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "No Date";

        const formattedTime = date
          ? date.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "numeric",
              hour12: true,
            })
          : "";

        return (
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-medium text-gray-900">
              {formattedDate}
            </span>
            {formattedTime && (
              <span className="text-xs text-gray-500">{formattedTime}</span>
            )}
          </div>
        );
      },
      valueGetter: ({ value }: any) => (value ? new Date(value) : null),
    },

    {
      field: "status",
      headerName: "Status",
      width: 150,
      valueGetter: (params: any) => {
        if (params.row.confirmed) return "Confirmed";
        if (params.row.rejected) return "Rejected";
        if (params.row.cancelled) return "Cancelled";
        return "Pending";
      },
      renderCell: (params: any) => {
        let bgColor = "bg-gray-100 text-gray-600";
        let Icon = ClockIcon; // default Pending icon

        if (params.value === "Confirmed") {
          bgColor = "bg-green-100 text-green-700";
          Icon = CheckCircleIcon;
        } else if (params.value === "Rejected") {
          bgColor = "bg-red-100 text-red-700";
          Icon = XCircleIcon;
        } else if (params.value === "Cancelled") {
          bgColor = "bg-orange-100 text-orange-700";
          Icon = ExclamationCircleIcon;
        }

        return (
          <span
            className={`flex items-center gap-1 px-3 py-0.5 rounded-full text-xs font-medium ${bgColor}`}
          >
            <Icon className="w-4 h-4" />
            {params.value}
          </span>
        );
      },
      filterable: true,
    },
  ] as const;

  const handleSubmitNote = async () => {
    try {
      if (selectedTenant) {
        await AddNote(tenantNote, selectedTenant.idProspectiveTenant);
        setTenantNote("");
        setTakeNote(false);
      }
    } catch (error) {
      console.log(`Failed to add Note ${error}`);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Assuming fetchAutomaticResponseHistory returns an array of AutomaticResponseHistory
        const paged: PagedResult<IAutomaticResponseHistory> =
          await fetchAutomaticResponseHistory(
            IsConfirmed ? "Confirmed" : "All",
            Last24Hrs,
            page,
            pageSize,
            debouncedSearchTerm,
          );
        const allEmailsWithTitle = new Set<{ email: string; dept: string }>();
        paged.items.forEach((row: any) =>
          allEmailsWithTitle.add({ email: row.email, dept: row.title }),
        );
        setAllEmails(allEmailsWithTitle);
        setRowCount(paged.totalCount ?? 0);
        setAutomaticResponseHistoryList(paged.items);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [
    takeNote,
    isTransferTenantToAppartmentOpen,
    searchParams,
    page,
    pageSize,
    debouncedSearchTerm,
    Last24Hrs,
    IsConfirmed,
  ]);

  useEffect(() => {}, [automaticResponseHistoryList]);

  return (
    <div className="relative flex flex-col mt-20">
      <div className="mx-4 sm:mx-9 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
        <div className="flex-shrink-0">
          <h1 className="text-base md:text-lg font-semibold leading-4 text-gray-900">
            Automatic Responses
          </h1>
          {/* <p className="mt-2 text-sm text-gray-700">
            A list of recommended appointments and basic info that was forwarded
            to prospective tenants, along with their respective responses.
          </p> */}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 md:gap-2.5 lg:gap-3">
          <button
            onClick={() => setIsRescheduleAppointmentOpen(true)}
            className="inline-flex items-center gap-1.5 py-2 px-3 md:px-4 text-xs md:text-sm font-medium rounded-lg 
             bg-orange-50 text-orange-700 hover:bg-orange-100 
             active:scale-[0.98] transition-all duration-200 whitespace-nowrap shadow-sm hover:shadow
             min-w-[120px] sm:min-w-[140px] justify-center"
          >
            <CalendarDaysIcon className="h-4 w-4" />
            <span>Reschedule Appointment</span>
          </button>

          <button
            onClick={() => setIsTransferTenantToAppartmentOpen(true)}
            className="inline-flex items-center gap-1.5 py-2 px-3 md:px-4 text-xs md:text-sm font-medium rounded-lg 
             bg-purple-50 text-purple-700 hover:bg-purple-100 
             active:scale-[0.98] transition-all duration-200 whitespace-nowrap shadow-sm hover:shadow
             min-w-[120px] sm:min-w-[140px] justify-center"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
            <span>Transfer Tenants</span>
          </button>

          <button
            onClick={() => setShowComposeEmail(true)}
            className="inline-flex items-center gap-1.5 py-2 px-3 md:px-4 text-xs md:text-sm font-medium rounded-lg 
             bg-green-50 text-green-700 hover:bg-green-100 
             active:scale-[0.98] transition-all duration-200 whitespace-nowrap shadow-sm hover:shadow
             min-w-[120px] sm:min-w-[140px] justify-center"
          >
            <PaperAirplaneIcon className="h-4 w-4 rotate-[-45deg]" />
            <span>Send Email</span>
          </button>
        </div>
      </div>

      <StyledDataGrid
        className="mt-5 rounded "
        getRowClassName={() =>
          "whitespace-nowrap text-gray-700 grid-row transition-all duration-300"
        }
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            searchTerm,
            setSearchTerm,
            setDebouncedSearchTerm,
          },
        }}
        sx={{
          ".MuiDataGrid-withBorderColor": {
            outline: "none !important",
          },

          marginLeft: { xs: "16px", sm: "36px", md: "36px" },
          marginRight: { xs: "16px", sm: "36px", md: "36px" },
          height: "80vh",
          width: {
            xs: "calc(100% - 32px)",
            sm: "calc(100% - 72px)",
            md: "calc(100% - 72px)",
          },
          maxWidth: { xs: "calc(100vw - 32px)", sm: "92vw", md: "92vw" },
          borderRadius: "10px !important",

          ".MuiDataGrid-columnHeaders": {
            background: "#f9fafb",
            color: "#111827",
            fontWeight: "700",
            fontSize: "0.95rem",
            borderBottom: "1px solid #e5e7eb",
          },

          ".MuiDataGrid-cell": {
            fontSize: "0.9rem",
            fontWeight: "500",
            padding: "14px 12px",
            color: "#374151",
            borderBottom: "1px solid #e3e3e3",
          },

          ".MuiDataGrid-row": {
            "&:hover": {
              backgroundColor: "#f9fbfb !important",
            },
            transition: "background-color 0.2s ease",
          },

          ".MuiDataGrid-footerContainer": {
            background: "#f9fafb",
            borderTop: "1px solid #e5e7eb",
          },
        }}
        getRowHeight={() => "auto"}
        rows={automaticResponseHistoryList}
        getRowId={(row) => row.id}
        columns={columns}
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        pageSizeOptions={[6, 8, 10, 15, 20]}
        disableRowSelectionOnClick
      />

      <ComposeEmail
        emails={allEmails}
        open={showComposeEmail}
        setOpen={setShowComposeEmail}
        selectedEmails={selectedRows}
      />

      <TransferTenantToAppartment
        open={isTransferTenantToAppartmentOpen}
        setOpen={setIsTransferTenantToAppartmentOpen}
        selectedTenants={selectedTenants}
      />

      <RescheduleAppointments
        open={isRescheduleAppointmentOpen}
        setOpen={setIsRescheduleAppointmentOpen}
        selectedTenants={selectedTenants}
      />

      {popoverPosition && selectedArhId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
          <div className="bg-white rounded-xl shadow-2xl border w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50/50">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Member Details
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Application mapping status
                </p>
              </div>
              <button
                onClick={() => {
                  setMemberGuarantorInfos([]);
                  setSelectedArhId("");
                  setPopoverPosition(null);
                }}
                className="p-1.5 rounded-full hover:bg-gray-200 text-gray-500 transition-colors"
              >
                <CloseOutlined className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {loading ? (
                <div className="py-12 flex flex-col items-center justify-center gap-3">
                  <Loading />
                  <span className="text-sm text-gray-400">
                    Loading members...
                  </span>
                </div>
              ) : memberGuarantorInfos.length > 0 ? (
                <div className="space-y-4">
                  {/* Tenants Section */}
                  <div>
                    <h3 className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                      Tenants
                    </h3>
                    <div className="space-y-2">
                      {memberGuarantorInfos.some((i) => !i.isGuarantor) ? (
                        memberGuarantorInfos.map((item) =>
                          !item.isGuarantor ? (
                            <div
                              key={item.id}
                              className="group relative bg-gray-50/50 rounded-lg p-3 border border-transparent hover:border-gray-200 hover:bg-white transition-all shadow-sm"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-gray-900 truncate">
                                      {item.name}
                                    </span>
                                    {item.isConfirmed && (
                                      <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    )}
                                  </div>
                                  {item.email && (
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                                      <EmailIcon
                                        className="w-3 h-3"
                                        style={{ fontSize: "14px" }}
                                      />
                                      <span className="truncate">
                                        {item.email}
                                      </span>
                                    </div>
                                  )}

                                  {item.mappedTo ? (
                                    <div className="mt-2 pt-2 border-t border-gray-100 bg-blue-50/20 rounded p-1.5">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1 rounded">
                                          MAPPED
                                        </span>
                                        <span className="text-xs font-medium text-gray-800 truncate">
                                          {item.mappedTo.name}
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
                                        <div className="flex items-center gap-1">
                                          <EmailIcon
                                            style={{ fontSize: "12px" }}
                                          />
                                          {item.mappedTo.email}
                                        </div>
                                        {item.mappedTo.phoneNo && (
                                          <div className="flex items-center gap-1">
                                            <PhoneIphoneIcon
                                              style={{ fontSize: "12px" }}
                                            />
                                            {item.mappedTo.phoneNo}
                                          </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                          🏠 {item.mappedTo.apartmentName}
                                        </div>
                                      </div>
                                    </div>
                                  ) : item.mappedToGroupOccupant ? (
                                    <div className="mt-2 pt-2 border-t border-gray-100 bg-sky-50/30 rounded p-1.5">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[9px] font-bold text-sky-600 bg-sky-50 px-1 rounded">
                                          MAPPED
                                        </span>
                                        <span className="text-xs font-medium text-gray-800 truncate">
                                          {item.mappedToGroupOccupant.name}
                                        </span>
                                        <span className="text-[8px] font-bold text-sky-600 bg-sky-100 px-1 rounded">
                                          AppFolio Group
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
                                        <div className="flex items-center gap-1">
                                          <EmailIcon
                                            style={{ fontSize: "12px" }}
                                          />
                                          {item.mappedToGroupOccupant.email}
                                        </div>
                                        {item.mappedToGroupOccupant.phone && (
                                          <div className="flex items-center gap-1">
                                            <PhoneIphoneIcon
                                              style={{ fontSize: "12px" }}
                                            />
                                            {item.mappedToGroupOccupant.phone}
                                          </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                          🏠 {item.mappedToGroupOccupant.apartmentTitle}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                      <span className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1">
                                        <ExclamationCircleIcon className="w-3 h-3 text-gray-400" />
                                        Unmapped
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : null,
                        )
                      ) : (
                        <p className="text-sm text-gray-400 italic pl-3.5">
                          No tenants added yet
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Guarantors Section */}
                  <div>
                    <h3 className="text-[10px] font-bold text-purple-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-600"></div>
                      Guarantors
                    </h3>
                    <div className="space-y-2">
                      {memberGuarantorInfos.some((i) => i.isGuarantor) ? (
                        memberGuarantorInfos.map((item) =>
                          item.isGuarantor ? (
                            <div
                              key={item.id}
                              className="group relative bg-gray-50/50 rounded-lg p-3 border border-transparent hover:border-gray-200 hover:bg-white transition-all shadow-sm"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-semibold text-gray-900 truncate">
                                      {item.name}
                                    </span>
                                    {item.isConfirmed && (
                                      <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    )}
                                  </div>
                                  {item.email && (
                                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
                                      <EmailIcon
                                        className="w-3 h-3"
                                        style={{ fontSize: "14px" }}
                                      />
                                      <span className="truncate">
                                        {item.email}
                                      </span>
                                    </div>
                                  )}

                                  {item.mappedTo ? (
                                    <div className="mt-2 pt-2 border-t border-gray-100 bg-purple-50/20 rounded p-1.5">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[9px] font-bold text-purple-600 bg-purple-50 px-1 rounded">
                                          MAPPED
                                        </span>
                                        <span className="text-xs font-medium text-gray-800 truncate">
                                          {item.mappedTo.name}
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
                                        <div className="flex items-center gap-1">
                                          <EmailIcon
                                            style={{ fontSize: "12px" }}
                                          />
                                          {item.mappedTo.email}
                                        </div>
                                        {item.mappedTo.phoneNo && (
                                          <div className="flex items-center gap-1">
                                            <PhoneIphoneIcon
                                              style={{ fontSize: "12px" }}
                                            />
                                            {item.mappedTo.phoneNo}
                                          </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                          🏠 {item.mappedTo.apartmentName}
                                        </div>
                                      </div>
                                    </div>
                                  ) : item.mappedToGroupOccupant ? (
                                    <div className="mt-2 pt-2 border-t border-gray-100 bg-sky-50/30 rounded p-1.5">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-[9px] font-bold text-sky-600 bg-sky-50 px-1 rounded">
                                          MAPPED
                                        </span>
                                        <span className="text-xs font-medium text-gray-800 truncate">
                                          {item.mappedToGroupOccupant.name}
                                        </span>
                                        <span className="text-[8px] font-bold text-sky-600 bg-sky-100 px-1 rounded">
                                          AppFolio Group
                                        </span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
                                        <div className="flex items-center gap-1">
                                          <EmailIcon
                                            style={{ fontSize: "12px" }}
                                          />
                                          {item.mappedToGroupOccupant.email}
                                        </div>
                                        {item.mappedToGroupOccupant.phone && (
                                          <div className="flex items-center gap-1">
                                            <PhoneIphoneIcon
                                              style={{ fontSize: "12px" }}
                                            />
                                            {item.mappedToGroupOccupant.phone}
                                          </div>
                                        )}
                                        <div className="flex items-center gap-1">
                                          🏠 {item.mappedToGroupOccupant.apartmentTitle}
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                      <span className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1">
                                        <ExclamationCircleIcon className="w-3 h-3 text-gray-400" />
                                        Unmapped
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ) : null,
                        )
                      ) : (
                        <p className="text-sm text-gray-400 italic pl-3.5">
                          No guarantors added yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center">
                  <p className="text-gray-500 text-sm italic">
                    No member information available for this inquiry.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {takeNote && (
        <div className="fixed top-0 left-0 z-50 bg-black/70 w-full h-screen flex items-center justify-center">
          <div className="max-w-sm w-full bg-white rounded-lg shadow-2xl border p-2">
            <div className="flex w-full items-center justify-between px-3 py-1.5">
              <span className="block text-sm font-medium text-gray-700">
                Write Note
              </span>
              <span
                onClick={() => setTakeNote(false)}
                className="cursor-pointer active:scale-95"
              >
                <CloseOutlined fontSize="small" />
              </span>
            </div>
            <div className="flex flex-col space-y-4 p-2">
              <textarea
                id="message"
                value={tenantNote ?? ""}
                onChange={(e) => setTenantNote(e.target.value)}
                rows={4}
                className="block p-2.5 w-full text-sm text-gray-900 bg-gray-50 rounded-lg border border-gray-300"
                placeholder="Write your Note..."
              ></textarea>
              <button
                onClick={() => handleSubmitNote()}
                className="inline-flex w-max items-center px-5 py-2.5 text-sm font-medium text-center text-white bg-blue-700 rounded-lg active:scale-[0.98]"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomaticResponseHistory;
